import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, List, Pagination, Popover, Spin, Switch, Tooltip, Typography, message } from 'antd';
import { AuditOutlined, BellOutlined, CheckCircleOutlined, CheckOutlined, ClockCircleOutlined, CloseCircleOutlined, KeyOutlined, MessageOutlined, PhoneOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { approvePasscodeRequest, denyPasscodeRequest } from '@/services/app/tenant/dispatch/route-lock';
import { history } from '@umijs/max';
import { dtFormat, fmtTime, formatPhoneNumber } from '@/util';
import {
  AppNotification,
  emitNotificationsUpdated,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  onNotificationsUpdated,
} from '@/services/app/notification';
import { getTenantContactsPaginated, markTenantConversationRead } from '@/services/app/tenant/call-board';
import {
  ensureNotificationSocketConnected,
  getNotificationSocketDebugInfo,
  NotificationSocketStatus,
  onNotificationSocketStatusChanged,
} from '@/services/app/websocket';
import { useJitteredReload } from '@/hooks/useJitteredReload';
import styles from './index.less';

const PAGE_SIZE = 10;

// Social Planner approval workflow (SocialPostApprovalNotifier) — clicking
// opens the post in the Planner (review drawer for approvers).
const SOCIAL_APPROVAL_REQUEST = 'social_post_approval_request';
const SOCIAL_APPROVAL_REVIEWED = 'social_post_approval_reviewed';
const SOCIAL_PLANNER_PATH = '/tenant/marketing/social/planner';

// Private callback due reminder (CallbackReminderService) — clicking opens the
// Call Board on the owner's "My Callbacks" tab.
const CALLBACK_DUE = 'callback_due';
// Time-off requests: approvers get a request (→ Time Off Requests page), the
// requester gets the decision (→ My Availability)
const TIME_OFF_REQUEST = 'time_off_request';
const TIME_OFF_REVIEWED = 'time_off_reviewed';
const TIME_OFF_REQUESTS_PATH = '/tenant/administration/users/time-off-requests';
const MY_AVAILABILITY_PATH = '/tenant/my-availability';
const MY_CALLBACKS_PATH = '/tenant/communications/call_board?queue=mine';

const WS_STATUS_DOT: Record<NotificationSocketStatus, { color: string; label: string }> = {
  open:         { color: '#52c41a', label: 'Live updates connected' },
  connecting:   { color: '#faad14', label: 'Live updates connecting…' },
  reconnecting: { color: '#faad14', label: 'Live updates reconnecting…' },
  disabled:     { color: '#bfbfbf', label: 'Live updates unavailable — polling only' },
};

/** A conversation with unread SMS, as getTenantContactsPaginated({ unread_only: 1 }) returns it. */
interface UnreadSmsContact {
  phone_number: string;
  name?: string;
  unread_count?: number;
  last_communication_preview?: string;
  last_communication_time?: string;
}

const sortNotifications = (notifications: AppNotification[]) =>
  [...notifications].sort((a, b) => {
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

const NotificationBell: React.FC = () => {
  const [open, setOpen]                   = useState(false);
  const [unread, setUnread]               = useState(0);
  const [loading, setLoading]             = useState(false);
  const [items, setItems]                 = useState<AppNotification[]>([]);
  const [smsContacts, setSmsContacts]     = useState<UnreadSmsContact[]>([]);
  const [smsOnly, setSmsOnly]             = useState(true);
  const [page, setPage]                   = useState(1);
  const [total, setTotal]                 = useState(0);
  const [wsStatus, setWsStatus]           = useState<NotificationSocketStatus>(
    () => getNotificationSocketDebugInfo().status,
  );

  // ----------------------------------------------------------------- badge refresh
  // No poll interval — the WS push (ensureNotificationSocketConnected) is the
  // only trigger for re-fetching after the initial mount; see
  // docs/frontend-polling-intervals.md.
  //
  // Deliberately does NOT re-emit emitNotificationsUpdated() when the count
  // changes — every consumer (Callboard, useUnreadDispatchNotifications, this
  // same fetchBadge) already subscribes directly to the original signal (WS
  // push, mark-read, mark-all), so a derived re-emit here was pure
  // redundancy. Worse, with no in-flight guard, overlapping fetchBadge()
  // calls could resolve out of order and stomp a ref with a stale count,
  // making the comparison look "changed" again — a self-sustaining loop that
  // also fanned out into Callboard's and the EDB hook's REST calls each cycle.
  const fetchBadge = useCallback(async () => {
    try {
      const { sms_count } = await getUnreadCount();
      setUnread(sms_count);
    } catch {
      // silently ignore
    }
  }, []);

  useEffect(() => {
    fetchBadge();
    ensureNotificationSocketConnected();
  }, [fetchBadge]);

  useEffect(() => onNotificationSocketStatusChanged(setWsStatus), []);

  // A server push reaches every open tab at once (incoming SMS, …) — spread
  // those re-fetches and fold a burst into one; this tab's own mark-read
  // actions still refresh the count immediately.
  const fetchBadgeFromPush = useJitteredReload(fetchBadge);

  useEffect(
    () => onNotificationsUpdated((source) => (source === 'push' ? fetchBadgeFromPush() : fetchBadge())),
    [fetchBadge, fetchBadgeFromPush],
  );

  // ----------------------------------------------------------------- list fetch
  const fetchList = useCallback(async (onlySms: boolean, pageNum: number) => {
    setLoading(true);
    try {
      if (onlySms) {
        // The SMS view lists unread conversations from the same counts as the badge — the SMS
        // Center's unread contacts — rather than notification rows, which are deleted after 7
        // days and don't exist for every unread message.
        const res = await getTenantContactsPaginated({
          page: pageNum,
          limit: PAGE_SIZE,
          filter: 'sms',
          skip_assigned_check: 1,
          unread_only: 1,
        });
        setSmsContacts(res.contacts || []);
        setTotal(res.pagination?.total || 0);
        setPage(res.pagination?.current_page || pageNum);
      } else {
        const result = await getNotifications({
          page: pageNum,
          perPage: PAGE_SIZE,
        });
        setItems(sortNotifications(result.notifications ?? []));
        setTotal(result.pagination?.total ?? 0);
        setPage(pageNum);
      }
    } catch {
      setItems([]);
      setSmsContacts([]);
      setTotal(0);
      setPage(1);
    } finally {
      setLoading(false);
    }
  }, []);

  // ----------------------------------------------------------------- popover open
  const handleOpenChange = useCallback(async (next: boolean) => {
    setOpen(next);
    if (!next) return;
    fetchList(smsOnly, 1);
  }, [fetchList, smsOnly]);

  // ----------------------------------------------------------------- sms filter
  const handleSmsOnlyChange = useCallback((checked: boolean) => {
    setSmsOnly(checked);
    fetchList(checked, 1);
  }, [fetchList]);

  // ----------------------------------------------------------------- pagination
  const handlePageChange = useCallback((nextPage: number) => {
    fetchList(smsOnly, nextPage);
  }, [fetchList, smsOnly]);

  // ----------------------------------------------------------------- mark single
  const handleMarkRead = useCallback(async (n: AppNotification) => {
    await markNotificationRead(n.id);
    if (n.data?.type === 'incoming_sms') {
      // The server marks the sender's whole conversation read, so every notification from
      // that number drops off — re-fetch the list; the badge re-fetches via the emit below.
      fetchList(smsOnly, page);
    } else {
      setItems((prev) => prev.filter((it) => it.id !== n.id));
    }
    emitNotificationsUpdated();
  }, [fetchList, smsOnly, page]);

  // ------------------------------------------------ approve/deny passcode request
  const handlePasscodeAction = useCallback(
    async (n: AppNotification, action: 'approve' | 'deny') => {
      const requestId = Number(n.data?.request_id);
      if (!requestId) return;
      try {
        if (action === 'approve') {
          await approvePasscodeRequest(requestId);
          message.success('Passcode sent to technician');
        } else {
          await denyPasscodeRequest(requestId);
          message.success('Request denied');
        }
        await markNotificationRead(n.id);
        setItems((prev) => prev.filter((it) => it.id !== n.id));
        emitNotificationsUpdated();
      } catch (e: any) {
        message.error(e?.message || 'Action failed');
      }
    },
    [],
  );

  // ------------------------------------------------------------- view all
  const handleViewAll = useCallback(() => {
    setOpen(false);
    history.push('/tenant/communications/notifications');
  }, []);

  // ------------------------------------------------------- SMS conversation items
  const openSmsConversation = useCallback((c: UnreadSmsContact) => {
    setOpen(false);
    history.push(`/tenant/communications/sms_board?phone=${encodeURIComponent(c.phone_number)}`);
  }, []);

  // Same as "Mark all as read" in the SMS Center thread.
  const handleMarkConversationRead = useCallback(async (c: UnreadSmsContact) => {
    try {
      await markTenantConversationRead(c.phone_number);
    } catch {
      message.error('Failed to mark messages as read');
      return;
    }
    fetchList(true, page);
    emitNotificationsUpdated();
  }, [fetchList, page]);

  // ----------------------------------------------------------------- mark all
  const handleMarkAll = useCallback(async () => {
    await markAllNotificationsRead();
    setItems([]);
    setSmsContacts([]);
    setTotal(0);
    setUnread(0);
    emitNotificationsUpdated();
  }, []);

  // ------------------------------------------------------------- click to open
  // For SMS notifications, jump to the SMS Center and auto-open the conversation
  // with the sender; for social post approvals, open the post in the Planner.
  // Marks a non-SMS notification read on the way out. An SMS stays unread until its
  // conversation is marked read in the SMS Center, where the thread shows it as new —
  // marking it here would mark the whole conversation read before the thread opens.
  const handleNotificationClick = useCallback((n: AppNotification) => {
    const data = n.data || {};
    const isSms = data.type === 'incoming_sms';
    const isSocial = data.type === SOCIAL_APPROVAL_REQUEST || data.type === SOCIAL_APPROVAL_REVIEWED;
    const isCallbackDue = data.type === CALLBACK_DUE;
    const isTimeOffRequest = data.type === TIME_OFF_REQUEST;
    const isTimeOffReviewed = data.type === TIME_OFF_REVIEWED;
    const phone = isSms ? data.from || data.customer_phone_number || '' : '';
    if (!phone && !(isSocial && data.post_id) && !isCallbackDue && !isTimeOffRequest && !isTimeOffReviewed) return;

    setOpen(false);
    if (!n.is_read && !isSms) {
      markNotificationRead(n.id).then(() => emitNotificationsUpdated()).catch(() => {});
      setItems((prev) => prev.filter((x) => x.id !== n.id));
    }
    history.push(
      isSms
        ? `/tenant/communications/sms_board?phone=${encodeURIComponent(phone)}`
        : isCallbackDue
          ? MY_CALLBACKS_PATH
          : isTimeOffRequest
            ? TIME_OFF_REQUESTS_PATH
            : isTimeOffReviewed
              ? MY_AVAILABILITY_PATH
              : `${SOCIAL_PLANNER_PATH}?post=${encodeURIComponent(String(data.post_id))}`,
    );
  }, []);

  // ----------------------------------------------------------------- render
  const content = (
    <div style={{ width: 340 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Text strong>Notifications</Typography.Text>
          <Switch
            size="small"
            checked={smsOnly}
            onChange={handleSmsOnlyChange}
            checkedChildren="SMS"
            unCheckedChildren="All"
          />
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {unread > 0 && (
            <Button size="small" type="link" icon={<CheckOutlined />} onClick={handleMarkAll}>
              Mark all read
            </Button>
          )}
          <Tooltip title="View all notifications">
            <Button size="small" type="text" icon={<UnorderedListOutlined />} onClick={handleViewAll} />
          </Tooltip>
        </span>
      </div>

      <div style={{ maxHeight: 546, overflowY: 'auto' }}>
      <Spin spinning={loading}>
      {(smsOnly ? smsContacts.length : items.length) === 0 ? (
        <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 24 }}>
          {loading ? ' ' : smsOnly ? 'No unread messages' : 'No notifications'}
        </Typography.Text>
      ) : smsOnly ? (
        <List
          dataSource={smsContacts}
          renderItem={(c) => (
            <List.Item
              onClick={() => openSmsConversation(c)}
              style={{ background: 'rgba(24,144,255,0.06)', padding: '8px 4px', borderRadius: 4, marginBottom: 2, cursor: 'pointer' }}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      <MessageOutlined style={{ color: '#1890ff', flexShrink: 0 }} />
                      <Typography.Text strong ellipsis>
                        {c.name && c.name !== c.phone_number ? c.name : formatPhoneNumber(c.phone_number)}
                      </Typography.Text>
                      <Badge count={c.unread_count || 0} size="small" />
                    </span>
                    <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                      <Tooltip title="Mark read">
                        <Button size="small" type="text" icon={<CheckOutlined />} onClick={(e) => { e.stopPropagation(); handleMarkConversationRead(c); }} />
                      </Tooltip>
                    </span>
                  </div>
                }
                description={
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                      {c.last_communication_preview}
                    </Typography.Text>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                      <ClockCircleOutlined style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }} />
                      <Tooltip title={dtFormat(c.last_communication_time)}>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {fmtTime(c.last_communication_time)}
                        </Typography.Text>
                      </Tooltip>
                    </div>
                  </>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <List
          dataSource={items}
          renderItem={(n) => {
            const isSms      = n.data?.type === 'incoming_sms';
            const isPasscode = n.data?.type === 'route_passcode_request';
            const isApprovalRequest  = n.data?.type === SOCIAL_APPROVAL_REQUEST;
            const isApprovalReviewed = n.data?.type === SOCIAL_APPROVAL_REVIEWED;
            const isCallbackDue      = n.data?.type === CALLBACK_DUE;
            const isTimeOff          = n.data?.type === TIME_OFF_REQUEST || n.data?.type === TIME_OFF_REVIEWED;
            const isClickable        = isSms || isApprovalRequest || isApprovalReviewed || isCallbackDue || isTimeOff;
            const actions    = isPasscode
              ? [
                  <Tooltip key="a" title="Approve">
                    <Button size="small" type="link" icon={<CheckCircleOutlined />} onClick={(e) => { e.stopPropagation(); handlePasscodeAction(n, 'approve'); }} />
                  </Tooltip>,
                  <Tooltip key="d" title="Deny">
                    <Button size="small" type="link" danger icon={<CloseCircleOutlined />} onClick={(e) => { e.stopPropagation(); handlePasscodeAction(n, 'deny'); }} />
                  </Tooltip>,
                ]
              : n.is_read
                ? []
                : [
                    <Tooltip key="r" title="Mark read">
                      <Button size="small" type="text" icon={<CheckOutlined />} onClick={(e) => { e.stopPropagation(); handleMarkRead(n); }} />
                    </Tooltip>
                  ];
            return (
            <List.Item
              onClick={isClickable ? () => handleNotificationClick(n) : undefined}
              style={{ background: n.is_read ? 'transparent' : 'rgba(24,144,255,0.06)', padding: '8px 4px', borderRadius: 4, marginBottom: 2, cursor: isClickable ? 'pointer' : 'default' }}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                      {isSms && <MessageOutlined style={{ color: '#1890ff', flexShrink: 0 }} />}
                      {isPasscode && <KeyOutlined style={{ color: '#faad14', flexShrink: 0 }} />}
                      {isApprovalRequest && <AuditOutlined style={{ color: '#faad14', flexShrink: 0 }} />}
                      {isCallbackDue && <PhoneOutlined style={{ color: '#fa8c16', flexShrink: 0 }} />}
                      {isApprovalReviewed && (n.data?.decision === 'denied'
                        ? <CloseCircleOutlined style={{ color: '#ff4d4f', flexShrink: 0 }} />
                        : <CheckCircleOutlined style={{ color: '#52c41a', flexShrink: 0 }} />)}
                      <Typography.Text strong={!n.is_read}>{n.title}</Typography.Text>
                    </span>
                    {actions.length > 0 && (
                      <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{actions}</span>
                    )}
                  </div>
                }
                description={
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{n.body}</Typography.Text>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 4 }}>
                      <ClockCircleOutlined style={{ fontSize: 11, color: 'rgba(0,0,0,0.45)' }} />
                      <Tooltip title={dtFormat(n.created_at, 'M/D/YYYY h:mm A')}>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                          {fmtTime(n.created_at)}
                        </Typography.Text>
                      </Tooltip>
                    </div>
                  </>
                }
              />
            </List.Item>
            );
          }}
        />
      )}
      </Spin>
      </div>

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
          <Pagination
            size="small"
            current={page}
            pageSize={PAGE_SIZE}
            total={total}
            onChange={handlePageChange}
            showSizeChanger={false}
            simple
          />
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomRight"
    >
      <span className={`${styles.action}`} style={{ cursor: 'pointer' }}>
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Badge count={unread} overflowCount={99} size="small">
            <BellOutlined style={{ fontSize: 18, color: '#fff' }} />
          </Badge>
          <Tooltip title={WS_STATUS_DOT[wsStatus].label}>
            <span
              style={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: WS_STATUS_DOT[wsStatus].color,
                border: '1px solid rgba(0,0,0,0.25)',
              }}
            />
          </Tooltip>
        </span>
      </span>
    </Popover>
  );
};

export default NotificationBell;
